import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ShieldCheck, Scale, LockKeyhole, Quote, ArrowRight } from "lucide-react";

import DifferentiatorCallout from "@/components/DifferentiatorCallout";
import NRISection from "@/components/NRISection";
import InitialAvatar from "@/components/InitialAvatar";
import HowItWorks from "@/components/HowItWorks";
import StatsStrip from "@/components/StatsStrip";
import PricingPlans from "@/components/PricingPlans";
import FinalCTA from "@/components/FinalCTA";
import CallbackDialog from "@/components/CallbackDialog";
import { TESTIMONIALS } from "@/data/testimonials";
import { FAQS } from "@/data/faqs";

const HERO_IMAGE = "/hero-family.png";

const WHY = [
  {
    icon: ShieldCheck,
    title: "Professionally Structured",
    body: "Our drafting engine prepares a professionally formatted will in seconds using deterministic clause logic aligned with Indian succession law.",
  },
  {
    icon: Scale,
    title: "Lawyer-Backed",
    body: "Our legal consultants are senior advocates specialising exclusively in succession, testamentary and estate planning law — ensuring every Will meets the highest legal standard.",
  },
  {
    icon: LockKeyhole,
    title: "100% Confidential",
    body: "Your family details are handled securely and confidentially. Payments are processed through Razorpay.",
  },
];

const TRUST_BADGES = [
  "Trusted by 500+ Indian Families",
  "Legally Valid under Succession Act, 1925",
  "Backed by Senior Supreme Court Advocates",
];

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section
        className="relative overflow-hidden bg-navy text-white"
        data-testid="home-hero"
      >
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-5 py-16 sm:px-8 sm:py-20 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14 lg:py-24">
          <div className="max-w-3xl">
            <div
              className="fade-up inline-flex items-center gap-2 rounded-full border border-gold/40 bg-white/5 px-4 py-1.5 text-[11px] uppercase tracking-[0.2em] text-gold backdrop-blur"
              data-testid="hero-tagline-pill"
            >
              Trusted India Will-Writing Platform
            </div>
            <h1 className="fade-up-1 mt-6 font-serif text-4xl leading-[1.08] sm:text-5xl lg:text-6xl">
              Your Family. Your Legacy. <br className="hidden sm:block" />
              <span className="text-gold">Protected.</span>
            </h1>
            <p className="fade-up-2 mt-6 max-w-2xl text-base leading-relaxed text-white/80 sm:text-lg">
              Create a legally structured Indian Will in minutes. Clear, guided,
              and designed to help your family avoid confusion later.
            </p>
            <div
              className="fade-up-2 mt-6 max-w-2xl rounded-md border-l-4 border-gold bg-white/[0.06] px-5 py-4 backdrop-blur"
              data-testid="hero-trust-callout"
            >
              <p className="font-serif text-base leading-relaxed text-white sm:text-lg">
                <span className="font-semibold text-gold">A premium Indian will-writing service.</span>{" "}
                Our legal consultants are senior advocates specialising exclusively in
                succession, testamentary and estate planning law — ensuring every Will
                meets the highest legal standard.
              </p>
            </div>
            <div className="fade-up-3 mt-9 flex flex-wrap gap-3">
              <Link to="/will-generator">
                <Button
                  size="lg"
                  className="bg-gold px-8 text-navy hover:bg-[#E5C87A]"
                  data-testid="hero-primary-cta"
                >
                  Create My Will – ₹99
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              <CallbackDialog
                size="lg"
                variant="outline"
                className="border-white/30 bg-white/5 px-8 text-white hover:border-gold hover:bg-white/10 hover:text-gold"
                label="Speak To Our Team"
                testId="hero-callback-dialog"
                buttonTestId="hero-secondary-cta"
              />
            </div>
            <p className="fade-up-3 mt-3 text-xs text-white/55">
              Prefer speaking first? Request a callback and our team will contact you.
            </p>
            <ul
              className="fade-up-3 mt-6 flex flex-wrap gap-x-6 gap-y-2 text-[13px] text-white/75"
              data-testid="hero-trust-strip"
            >
              <li className="flex items-center gap-2">
                <span className="text-gold">✔</span> Structured under Indian Succession principles
              </li>
              <li className="flex items-center gap-2">
                <span className="text-gold">✔</span> Instant PDF &amp; DOCX download
              </li>
              <li className="flex items-center gap-2">
                <span className="text-gold">✔</span> Optional Senior Lawyer Review available
              </li>
            </ul>

            <div className="fade-up-3 mt-12 grid max-w-2xl grid-cols-1 gap-3 sm:grid-cols-3">
              {TRUST_BADGES.map((b, i) => (
                <div
                  key={i}
                  className="rounded-md border border-white/10 bg-white/[0.04] px-4 py-3 text-xs leading-snug text-white/85 backdrop-blur"
                  data-testid={`hero-trust-badge-${i}`}
                >
                  <div className="text-gold">✦</div>
                  <div className="mt-1">{b}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right column: family photo (mobile: stacks below text) */}
          <div className="relative w-full" data-testid="hero-image-column">
            <img
              src={HERO_IMAGE}
              alt="An Indian family across generations together at home"
              className="w-full h-auto max-h-[560px] rounded-md object-cover"
              data-testid="hero-family-image"
            />
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <StatsStrip />

      {/* HOW IT WORKS */}
      <HowItWorks />

      {/* WHY WILLSEVA */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-24" data-testid="why-section">
        <div className="max-w-2xl">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">
            Why WillSeva
          </div>
          <h2 className="mt-3 font-serif text-3xl text-navy sm:text-4xl">
            A seva, not a service.
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-[#4A5568] sm:text-base">
            Writing a will is an act of love. We built WillSeva to honour that — with a
            professionally structured drafting engine, senior legal review on demand,
            and complete confidentiality.
          </p>
        </div>

        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {WHY.map((w, i) => {
            const Icon = w.icon;
            return (
              <Card
                key={i}
                className="hover-lift border-[#0A1128]/10 shadow-sm"
                data-testid={`why-card-${i}`}
              >
                <CardContent className="p-8">
                  <div className="flex h-12 w-12 items-center justify-center rounded-md bg-navy text-gold">
                    <Icon size={22} />
                  </div>
                  <h3 className="mt-6 font-serif text-xl text-navy">{w.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#4A5568]">{w.body}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* DIFFERENTIATOR */}
      <section className="mx-auto max-w-7xl px-5 pb-20 sm:px-8">
        <DifferentiatorCallout testId="home-differentiator" />
      </section>

      {/* PRICING (on homepage) */}
      <section className="bg-[#F9FAFB] py-20 sm:py-24" data-testid="home-pricing-section">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">
              Pricing
            </div>
            <h2 className="mt-3 font-serif text-3xl text-navy sm:text-4xl">
              Transparent pricing. <span className="text-gold">Lifetime peace of mind.</span>
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-[#4A5568] sm:text-base">
              One-time payment for the Essential Will. Optionally add Senior Lawyer Review for end-to-end assurance.
            </p>
          </div>
          <div className="mt-12">
            <PricingPlans testIdPrefix="home-pricing" />
          </div>
        </div>
      </section>

      {/* NRI SECTION */}
      <NRISection />

      {/* LEGAL TEAM */}
      <section className="bg-[#F9FAFB] py-20 sm:py-24" data-testid="legal-team-section">
        <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">
            Our Legal Team
          </div>
          <h2 className="mt-3 font-serif text-3xl text-navy sm:text-4xl">
            Led by senior advocates. Delivered with care.
          </h2>
          <p
            className="mx-auto mt-6 max-w-3xl text-sm leading-relaxed text-[#4A5568] sm:text-base"
            data-testid="legal-team-trust-line"
          >
            Our legal consultants are senior advocates specialising exclusively in
            succession, testamentary and estate planning law — ensuring every Will
            meets the highest legal standard.
          </p>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-24" data-testid="testimonials-section">
        <div className="max-w-2xl">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">
            Stories From Families
          </div>
          <h2 className="mt-3 font-serif text-3xl text-navy sm:text-4xl">
            500+ Indian families, one legacy at a time.
          </h2>
        </div>

        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {TESTIMONIALS.map((t, i) => (
            <Card
              key={i}
              className="hover-lift relative overflow-hidden border-[#0A1128]/10 shadow-sm"
              data-testid={`testimonial-card-${i}`}
            >
              <div className="pointer-events-none absolute -right-4 -top-4 text-gold/10">
                <Quote size={96} strokeWidth={1} />
              </div>
              <CardContent className="relative p-8">
                <p className="font-serif text-base leading-relaxed text-navy">
                  "{t.quote}"
                </p>
                <div className="mt-6 flex items-center gap-3 border-t border-[#0A1128]/10 pt-4">
                  <InitialAvatar name={t.name} size={42} variant="gold" />
                  <div>
                    <div className="font-serif text-sm font-semibold text-navy">{t.name}</div>
                    <div className="text-xs text-[#4A5568]">{t.city}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#F9FAFB] py-20 sm:py-24" data-testid="faq-section">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">
            Frequently Asked Questions
          </div>
          <h2 className="mt-3 font-serif text-3xl text-navy sm:text-4xl">
            Everything you'd want to ask a lawyer — answered.
          </h2>

          <Accordion type="single" collapsible className="mt-8 w-full">
            {FAQS.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border-b border-[#0A1128]/10"
              >
                <AccordionTrigger
                  className="text-left font-serif text-base text-navy sm:text-lg"
                  data-testid={`faq-trigger-${i}`}
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-[#4A5568] sm:text-base">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="mt-10 flex flex-col items-start gap-3 rounded-md border border-[#0A1128]/10 bg-white p-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="font-serif text-lg text-navy">
              Have Questions? Talk To Us.
            </div>
            <CallbackDialog
              label="Request a Callback"
              className="bg-navy text-white hover:bg-[#1A2642]"
              testId="faq-callback-dialog"
              buttonTestId="faq-contact-cta"
            />
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <FinalCTA />
    </div>
  );
}
